import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import api from '../../services/api';
import { TreeView, TreeItem } from '@mui/x-tree-view';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import PermMediaOutlinedIcon from '@mui/icons-material/PermMediaOutlined';
import { alpha, useTheme, type SxProps, type Theme } from '@mui/material/styles';
import SplitPane from '../layout/SplitPane';
import {
  contentHasMedia,
  isContentCategoryEmptyForDisplay,
  getContentMediaIndicatorTooltip,
} from '../../utils/CategoryUtils';
import { Category } from '../../types/Category';
import { Content } from '../../types/Content';
import {
  DocumentCompareSession,
  DocumentCompareSide,
  CompareNodeStatus,
  CompareMergeNewDocumentBase,
  MergeCompareContentSelection,
  MergeCompareTypeConflictDirection,
  typeConflictMergeTargetsForRows,
} from '../../types/DocumentCompare';
import { MergeMissingResolve, MergeTypeMismatchResolve } from '../../types/MergeTypes';
import {
  buildCategoryPathMap,
  buildComparedChildCategoryRows,
  buildComparedContentRows,
  buildComparedSubtreeContentRows,
  buildSingleSideChildCategoryRows,
  buildSingleSideContentRows,
  buildTreeCategoryCopyToLeft,
  buildTreeCategoryCopyToRight,
  collectSubtreeContentEntries,
  comparedCategoryCanSelectForMerge,
  comparedCategorySelectionKey,
  comparedRowCanSelectForMerge,
  comparedRowSelectionKey,
  collectSideCompareSubtreePathKeys,
  COMPARE_ROOT_PATH_KEY,
  ComparedContentRowStatus,
  compareTreeNodeId,
  expandedPathKeysToNodeIds,
  findCategoryByPath,
  branchSubtreeHasCompareDifferences,
  compareDocumentsFullyMatch,
  comparePathSelectedOnSide,
  compareTreeSelectedNodeId,
  getNodeCompareStatus,
  getNodeCompareStatusForSide,
  hasOneSidedBranchAncestor,
  pathHasTypeConflict,
  categoryIsBranchCategory,
  categoryIsContentCategory,
  listSideCompareTreeChildren,
  nodeIdToPathKey,
  relPathToKey,
} from '../../utils/documentCompareUtils';
import { DropdownFieldTooltip } from '../DropdownFieldTooltip';
import { getContentPanePreviewText } from '../../utils/contentRichPreviewHtml';
import { useHelpContent } from '../../utils/HelpUtils';
import { HelpContentKey } from '../../constants/helpContent';

/** Minimum subcategories that must be selected to copy a one-sided branch selectively. */
const MIN_ONE_SIDED_BRANCH_SUBCATEGORIES = 2;

const compareContentStatusHelpKey: Record<ComparedContentRowStatus, HelpContentKey> = {
  both: 'document-merge-compare-content-both',
  'left-only': 'document-merge-compare-content-left-only',
  'right-only': 'document-merge-compare-content-right-only',
};

function suggestMergeToNewDocumentName(baseSide: DocumentCompareSide | null): string {
  const baseLabel = baseSide?.label?.trim();
  if (!baseLabel) {
    return 'Document — merged';
  }
  return `${baseLabel} — merged`;
}

function CompareHelpTooltip({
  helpKey,
  enabled = true,
  placement = 'top',
  block = false,
  children,
}: {
  helpKey: HelpContentKey;
  enabled?: boolean;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  block?: boolean;
  children: React.ReactElement;
}) {
  const { getTooltipTextForState } = useHelpContent();
  const title = getTooltipTextForState(helpKey, enabled);
  if (!title) {
    return children;
  }
  return (
    <Tooltip title={title} placement={placement}>
      <Box
        component="span"
        sx={{
          display: block ? 'block' : 'inline-flex',
          height: block ? '100%' : undefined,
          width: block ? '100%' : undefined,
          minHeight: block ? 0 : undefined,
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
}

const compareDocumentTreeViewSx: SxProps<Theme> = (theme) => ({
  '& .MuiTreeItem-content': {
    padding: theme.spacing(0, 0.5),
    minHeight: 28,
    borderRadius: 1,
    '&:hover': {
      backgroundColor: 'transparent !important',
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent !important',
    },
    '&.Mui-selected': {
      backgroundColor: `${alpha(theme.palette.primary.main, 0.12)} !important`,
      '&:hover': {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.12)} !important`,
      },
      '&.Mui-focused': {
        backgroundColor: `${alpha(theme.palette.primary.main, 0.12)} !important`,
      },
    },
  },
  '& .MuiTreeItem-group': {
    marginLeft: 0,
    paddingLeft: theme.spacing(2),
  },
});

interface DocumentCompareViewProps {
  session: DocumentCompareSession;
  onMergeSelectedContent?: (selection: MergeCompareContentSelection) => Promise<void>;
  /** Undo the most recent compare copy (content/category) on the target document. */
  onUndoLastCopy?: () => Promise<void>;
  canUndoLastCopy?: boolean;
  /** Full document merge: duplicate the chosen side under a new name and merge the other side into it. */
  onMergeDocumentsToNew?: (
    newName: string,
    base: CompareMergeNewDocumentBase,
  ) => Promise<void>;
  existingDocumentNames?: string[];
  onMergeSettingsChange?: (
    resolveMissing: MergeMissingResolve,
    resolveTypeMismatch: MergeTypeMismatchResolve,
  ) => void;
  mergeApplying?: boolean;
}

const statusLabel: Record<CompareNodeStatus, string> = {
  match: 'Match',
  'has-child-diff': 'Has differences',
  'missing-content': 'Missing content',
  'left-only': 'Left only',
  'right-only': 'Right only',
  'type-conflict': 'Type conflict',
  unpaired: '',
};

const statusColor: Record<
  CompareNodeStatus,
  'default' | 'success' | 'warning' | 'error' | 'info'
> = {
  match: 'success',
  'has-child-diff': 'warning',
  'missing-content': 'warning',
  'left-only': 'info',
  'right-only': 'warning',
  'type-conflict': 'error',
  unpaired: 'default',
};

const contentStatusLabel: Record<ComparedContentRowStatus, string> = {
  both: 'Both',
  'left-only': 'Left only',
  'right-only': 'Right only',
};

const contentStatusChipColor: Record<
  ComparedContentRowStatus,
  'default' | 'success' | 'warning' | 'info'
> = {
  both: 'success',
  'left-only': 'info',
  'right-only': 'warning',
};

function formatContentPreview(content: Content): string {
  const text = getContentPanePreviewText(content.text, content.richTextHtml).trim();
  if (text) {
    return text.length > 100 ? `${text.substring(0, 100)}...` : text;
  }
  return contentHasMedia(content) ? '(media only)' : '';
}

function renderCompareChildCategoryTypeIcon(
  category: Category | undefined,
  compareStatus?: CompareNodeStatus,
) {
  if (compareStatus === 'type-conflict') {
    return (
      <ReportProblemOutlinedIcon
        sx={{ color: 'error.main', fontSize: 20 }}
        aria-label="Type conflict"
      />
    );
  }
  if (!category) {
    return null;
  }
  if (category.contentCategory) {
    return isContentCategoryEmptyForDisplay(category, null) ? (
      <DescriptionOutlinedIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
    ) : (
      <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
    );
  }
  const hasChildren = (category.children?.length ?? 0) > 0;
  return hasChildren ? (
    <FolderIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
  ) : (
    <DescriptionIcon sx={{ color: 'text.secondary', fontSize: 20 }} aria-hidden />
  );
}

function CompareContentStatusChip({ status }: { status: ComparedContentRowStatus }) {
  const { getTooltipTextForState } = useHelpContent();
  const helpKey = compareContentStatusHelpKey[status];
  const chip = (
    <Chip
      size="small"
      label={contentStatusLabel[status]}
      color={contentStatusChipColor[status]}
      variant="outlined"
      sx={{ height: 22, fontSize: '0.7rem' }}
    />
  );
  const title = getTooltipTextForState(helpKey, true);
  if (!title) {
    return chip;
  }
  return (
    <Tooltip title={title} placement="top">
      <span style={{ display: 'inline-flex' }}>{chip}</span>
    </Tooltip>
  );
}

function CompareStatusChip({ status }: { status: CompareNodeStatus }) {
  if (status === 'unpaired') {
    return null;
  }
  return (
    <Chip
      size="small"
      label={statusLabel[status]}
      color={statusColor[status]}
      variant="outlined"
      sx={{ ml: 0.75, height: 20, fontSize: '0.7rem' }}
    />
  );
}

function renderTreeNodes(
  leftRoot: Category,
  rightRoot: Category,
  parentRelPath: string[],
  side: 'left' | 'right',
  selectedTreeSide: 'left' | 'right',
  selectedPath: string[] | null,
  onSelectPath: (path: string[]) => void,
  onRowDoubleClick: (relPath: string[], hasChildren: boolean, e: React.MouseEvent) => void,
): React.ReactNode[] {
  const sideRoot = side === 'left' ? leftRoot : rightRoot;
  const children = listSideCompareTreeChildren(sideRoot, parentRelPath);

  return children.map(({ displayName, relPath: childPath }) => {
    const leftNode = findCategoryByPath(leftRoot, childPath) ?? undefined;
    const rightNode = findCategoryByPath(rightRoot, childPath) ?? undefined;
    const parentTypeConflict = pathHasTypeConflict(leftRoot, rightRoot, parentRelPath);
    const status = getNodeCompareStatusForSide(leftNode, rightNode, side, {
      parentTypeConflict,
      treeContext: { leftRoot, rightRoot, relPath: childPath },
    });
    const node = side === 'left' ? leftNode : rightNode;
    if (!node) {
      return null;
    }
    const pathKey = childPath.join('/');
    const hasBranchChildren = listSideCompareTreeChildren(sideRoot, childPath).length > 0;
    const hasChildren = hasBranchChildren && !node.contentCategory;

    const label = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
        }}
        onClick={(e) => {
          e.stopPropagation();
          onSelectPath(childPath);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!hasChildren) {
            return;
          }
          onRowDoubleClick(childPath, true, e);
        }}
      >
        {node.contentCategory ? (
          <DescriptionIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
        ) : (
          <FolderIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
        )}
        <Typography variant="body2" component="span">
          {displayName}
        </Typography>
        <CompareStatusChip status={status} />
      </Box>
    );

    const nodeId = compareTreeNodeId(side, node.id);

    if (node.contentCategory || !hasChildren) {
      return <TreeItem key={nodeId} nodeId={nodeId} label={label} />;
    }

    return (
      <TreeItem key={nodeId} nodeId={nodeId} label={label}>
        {renderTreeNodes(
          leftRoot,
          rightRoot,
          childPath,
          side,
          selectedTreeSide,
          selectedPath,
          onSelectPath,
          onRowDoubleClick,
        )}
      </TreeItem>
    );
  }).filter((node): node is React.ReactElement => node != null);
}

const DocumentCompareView: React.FC<DocumentCompareViewProps> = ({
  session,
  onMergeSelectedContent,
  onUndoLastCopy,
  canUndoLastCopy = false,
  onMergeDocumentsToNew,
  existingDocumentNames = [],
  onMergeSettingsChange,
  mergeApplying = false,
}) => {
  const resolveMissing =
    session.resolveMissing ?? MergeMissingResolve.COPY;
  const resolveTypeMismatch =
    session.resolveTypeMismatch ?? MergeTypeMismatchResolve.KEEP;
  const theme = useTheme();
  const { getTooltipTextForState } = useHelpContent();
  const [selectedPath, setSelectedPath] = useState<string[] | null>(null);
  const [mergeIntoNewDocument, setMergeIntoNewDocument] = useState(false);
  const [mergeToNewDialogOpen, setMergeToNewDialogOpen] = useState(false);
  const [mergeToNewPendingBase, setMergeToNewPendingBase] =
    useState<CompareMergeNewDocumentBase | null>(null);
  const [mergeToNewName, setMergeToNewName] = useState('');
  const [mergeToNewNameError, setMergeToNewNameError] = useState('');
  const [mergeToNewSubcategories, setMergeToNewSubcategories] = useState<string[]>([]);
  const [loadingMergeToNewSubcategories, setLoadingMergeToNewSubcategories] = useState(false);
  const [selectedTreeSide, setSelectedTreeSide] = useState<'left' | 'right'>('left');
  const [selectedRowKeys, setSelectedRowKeys] = useState<Set<string>>(new Set());

  const rightIsReadOnlyShared = session.right.readAccessGrantId != null;

  const mergeToNewBaseSide = useMemo((): DocumentCompareSide | null => {
    if (mergeToNewPendingBase === 'left') {
      return session.left;
    }
    if (mergeToNewPendingBase === 'right') {
      return session.right;
    }
    return null;
  }, [mergeToNewPendingBase, session.left, session.right]);

  const mergeToNewOtherSide = useMemo((): DocumentCompareSide | null => {
    if (mergeToNewPendingBase === 'left') {
      return session.right;
    }
    if (mergeToNewPendingBase === 'right') {
      return session.left;
    }
    return null;
  }, [mergeToNewPendingBase, session.left, session.right]);

  const documentsFullyMatch = useMemo(
    () => compareDocumentsFullyMatch(session.leftRoot, session.rightRoot),
    [session.leftRoot, session.rightRoot],
  );

  const isRootSelection =
    selectedPath != null && selectedPath.length === 0;

  const showMergeIntoNewDocumentOption =
    isRootSelection && !documentsFullyMatch && !!onMergeDocumentsToNew;

  useEffect(() => {
    if (!isRootSelection) {
      setMergeIntoNewDocument(false);
    }
  }, [isRootSelection]);

  const normalizeDocumentName = (s: string) =>
    (s || '')
      .normalize('NFKC')
      .replace(/[\u200B-\u200D\uFEFF]/g, '')
      .replace(/\u00A0/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const validateMergeToNewDocumentName = (
    trimmedName: string,
    subs: string[] = mergeToNewSubcategories,
    baseSide: DocumentCompareSide | null = mergeToNewBaseSide,
  ): string => {
    if (!trimmedName) {
      return 'Document name cannot be empty';
    }
    const nn = normalizeDocumentName(trimmedName);
    const baseNorm = baseSide ? normalizeDocumentName(baseSide.label) : '';
    if (nn !== baseNorm && existingDocumentNames.some(
      (name) => normalizeDocumentName(name) === nn,
    )) {
      return 'A document with this name already exists';
    }
    if (subs.map(normalizeDocumentName).includes(nn)) {
      return 'Document contains a sub category with this name';
    }
    return '';
  };

  useEffect(() => {
    const baseSide = mergeToNewBaseSide;
    if (!mergeToNewDialogOpen || !baseSide?.categoryId) {
      return;
    }
    const { categoryId, documentName } = baseSide;
    let cancelled = false;
    setLoadingMergeToNewSubcategories(true);
    (async () => {
      try {
        await api.post('/documents/open', null, {
          params: { documentId: documentName },
        });
        const res = await api.get('/categories/subcategories', {
          params: { categoryId },
        });
        if (!cancelled) {
          setMergeToNewSubcategories(
            (res.data as { name: string }[]).map((c) => c.name),
          );
        }
      } catch {
        if (!cancelled) {
          setMergeToNewSubcategories([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingMergeToNewSubcategories(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mergeToNewDialogOpen, mergeToNewPendingBase, mergeToNewBaseSide]);

  useEffect(() => {
    if (!mergeToNewDialogOpen) {
      return;
    }
    const trimmed = mergeToNewName.trim();
    if (!trimmed) {
      setMergeToNewNameError('');
      return;
    }
    setMergeToNewNameError(validateMergeToNewDocumentName(trimmed));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- revalidate when library or subcases change
  }, [
    mergeToNewDialogOpen,
    mergeToNewName,
    mergeToNewSubcategories,
    existingDocumentNames,
    mergeToNewBaseSide?.label,
  ]);

  const openMergeToNewDocumentDialog = (base: CompareMergeNewDocumentBase) => {
    const baseSide = base === 'left' ? session.left : session.right;
    const suggested = suggestMergeToNewDocumentName(baseSide);
    setMergeToNewPendingBase(base);
    setMergeToNewName(suggested);
    setMergeToNewNameError(
      validateMergeToNewDocumentName(suggested.trim(), [], baseSide),
    );
    setMergeToNewDialogOpen(true);
  };

  const closeMergeToNewDocumentDialog = () => {
    setMergeToNewDialogOpen(false);
    setMergeToNewPendingBase(null);
    setMergeToNewName('');
    setMergeToNewNameError('');
  };

  const handleMergeToNewDocumentConfirm = async () => {
    if (!onMergeDocumentsToNew || mergeToNewPendingBase == null) {
      return;
    }
    const trimmed = mergeToNewName.trim();
    const err = validateMergeToNewDocumentName(trimmed);
    if (err) {
      setMergeToNewNameError(err);
      return;
    }
    try {
      await onMergeDocumentsToNew(trimmed, mergeToNewPendingBase);
      closeMergeToNewDocumentDialog();
    } catch {
      // MainLayout shows the error alert
    }
  };

  const leftRootNodeId = compareTreeNodeId('left', session.leftRoot.id);
  const rightRootNodeId = compareTreeNodeId('right', session.rightRoot.id);

  const leftPathMap = useMemo(
    () => buildCategoryPathMap(session.leftRoot),
    [session.leftRoot],
  );
  const rightPathMap = useMemo(
    () => buildCategoryPathMap(session.rightRoot),
    [session.rightRoot],
  );

  const [expandedPathKeys, setExpandedPathKeys] = useState<string[]>(() => [COMPARE_ROOT_PATH_KEY]);

  const leftExpanded = useMemo(
    () => expandedPathKeysToNodeIds('left', session.leftRoot, expandedPathKeys),
    [expandedPathKeys, session.leftRoot],
  );
  const rightExpanded = useMemo(
    () => expandedPathKeysToNodeIds('right', session.rightRoot, expandedPathKeys),
    [expandedPathKeys, session.rightRoot],
  );

  const togglePathKey = (pathKey: string) => {
    setExpandedPathKeys((prev) =>
      prev.includes(pathKey) ? prev.filter((k) => k !== pathKey) : [...prev, pathKey],
    );
  };

  const toggleSubtreePathKeys = (relPath: string[], side: 'left' | 'right') => {
    const keys = collectSideCompareSubtreePathKeys(
      side === 'left' ? session.leftRoot : session.rightRoot,
      relPath,
    );
    if (keys.length === 0) {
      return;
    }
    setExpandedPathKeys((prev) => {
      const fullyExpanded = keys.every((k) => prev.includes(k));
      if (fullyExpanded) {
        const remove = new Set(keys);
        return prev.filter((k) => !remove.has(k));
      }
      return Array.from(new Set([...prev, ...keys]));
    });
  };

  const handleRowDoubleClick = (
    relPath: string[],
    hasChildren: boolean,
    side: 'left' | 'right',
    e: React.MouseEvent,
  ) => {
    if (!hasChildren) {
      return;
    }
    const pathKey = relPathToKey(relPath);
    if (e.metaKey || e.ctrlKey) {
      toggleSubtreePathKeys(relPath, side);
      return;
    }
    togglePathKey(pathKey);
  };

  const handleChevronToggle = (side: 'left' | 'right', nodeIds: string[]) => {
    const prevIds = side === 'left' ? leftExpanded : rightExpanded;
    const toggled =
      nodeIds.find((id) => !prevIds.includes(id)) ??
      prevIds.find((id) => !nodeIds.includes(id));
    if (!toggled) {
      return;
    }
    const rootName = side === 'left' ? session.leftRoot.name : session.rightRoot.name;
    const pathMap = side === 'left' ? leftPathMap : rightPathMap;
    const pathKey = nodeIdToPathKey(side, toggled, pathMap, rootName);
    if (pathKey) {
      togglePathKey(pathKey);
    }
  };

  const selectPathFromNodeId = (side: 'left' | 'right', nodeId: string) => {
    const rootName = side === 'left' ? session.leftRoot.name : session.rightRoot.name;
    const pathMap = side === 'left' ? leftPathMap : rightPathMap;
    const pathKey = nodeIdToPathKey(side, nodeId, pathMap, rootName);
    if (!pathKey) {
      return;
    }
    setSelectedTreeSide(side);
    if (pathKey === relPathToKey([])) {
      setSelectedPath([]);
      return;
    }
    setSelectedPath(pathKey.split('/'));
  };

  const selectPath = (side: 'left' | 'right', path: string[]) => {
    setSelectedTreeSide(side);
    setSelectedPath(path);
  };

  const renderRootTreeItem = (
    side: 'left' | 'right',
    root: Category,
    otherRoot: Category,
    rootNodeId: string,
    rootLabel: string,
  ) => {
    const leftNode = side === 'left' ? root : otherRoot;
    const rightNode = side === 'right' ? root : otherRoot;
    const leftDocRoot = side === 'left' ? root : otherRoot;
    const rightDocRoot = side === 'right' ? root : otherRoot;
    const status = getNodeCompareStatusForSide(leftNode, rightNode, side, {
      treeContext: { leftRoot: leftDocRoot, rightRoot: rightDocRoot, relPath: [] },
    });
    const hasSubtree = listSideCompareTreeChildren(root, []).length > 0;

    const label = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
        }}
        onClick={(e) => {
          e.stopPropagation();
          selectPath(side, []);
        }}
        onDoubleClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!hasSubtree) {
            return;
          }
          handleRowDoubleClick([], hasSubtree, side, e);
        }}
      >
        <FolderIcon sx={{ fontSize: 18, mr: 0.5, opacity: 0.8 }} />
        <Typography variant="body2" component="span">
          {rootLabel}
        </Typography>
        <CompareStatusChip status={status} />
      </Box>
    );

    if (!hasSubtree) {
      return <TreeItem nodeId={rootNodeId} label={label} />;
    }

    return (
      <TreeItem nodeId={rootNodeId} label={label}>
        {renderTreeNodes(
          leftNode,
          rightNode,
          [],
          side,
          selectedTreeSide,
          selectedPath,
          (path) => selectPath(side, path),
          (relPath, hasChildren, e) => handleRowDoubleClick(relPath, hasChildren, side, e),
        )}
      </TreeItem>
    );
  };

  const selectedLeft =
    selectedPath == null
      ? null
      : selectedPath.length === 0
        ? session.leftRoot
        : findCategoryByPath(session.leftRoot, selectedPath);
  const selectedRight =
    selectedPath == null
      ? null
      : selectedPath.length === 0
        ? session.rightRoot
        : findCategoryByPath(session.rightRoot, selectedPath);

  const leftTreeSelectedNodeId = useMemo(
    () =>
      compareTreeSelectedNodeId(
        'left',
        session.leftRoot,
        selectedPath,
        selectedTreeSide,
        session.leftRoot,
        session.rightRoot,
      ),
    [selectedPath, selectedTreeSide, session.leftRoot, session.rightRoot],
  );
  const rightTreeSelectedNodeId = useMemo(
    () =>
      compareTreeSelectedNodeId(
        'right',
        session.rightRoot,
        selectedPath,
        selectedTreeSide,
        session.leftRoot,
        session.rightRoot,
      ),
    [selectedPath, selectedTreeSide, session.leftRoot, session.rightRoot],
  );

  const isSingleSidedCategorySelection =
    (selectedLeft != null) !== (selectedRight != null);

  const isTypeConflictSelection = useMemo(
    () =>
      selectedLeft != null &&
      selectedRight != null &&
      selectedLeft.contentCategory !== selectedRight.contentCategory,
    [selectedLeft, selectedRight],
  );

  const treeSelectionStatus = useMemo((): CompareNodeStatus | null => {
    if (selectedPath == null || selectedPath.length === 0) {
      return null;
    }
    const parentTypeConflict =
      selectedPath.length > 0 &&
      pathHasTypeConflict(
        session.leftRoot,
        session.rightRoot,
        selectedPath.slice(0, -1),
      );
    return getNodeCompareStatusForSide(
      selectedLeft ?? undefined,
      selectedRight ?? undefined,
      selectedTreeSide,
      { parentTypeConflict },
    );
  }, [selectedPath, selectedLeft, selectedRight, selectedTreeSide, session.leftRoot, session.rightRoot]);

  const treeSelectionRowKey =
    selectedPath != null && selectedPath.length > 0 ? relPathToKey(selectedPath) : '';

  const treeCategoryCopyToLeft = useMemo(() => {
    if (
      treeSelectionStatus !== 'right-only' ||
      !selectedRight ||
      selectedPath == null
    ) {
      return null;
    }
    return buildTreeCategoryCopyToLeft(
      session.leftRoot,
      selectedPath,
      selectedRight,
    );
  }, [treeSelectionStatus, selectedRight, selectedPath, session.leftRoot]);

  const treeCategoryCopyToRight = useMemo(() => {
    if (
      treeSelectionStatus !== 'left-only' ||
      !selectedLeft ||
      rightIsReadOnlyShared ||
      selectedPath == null
    ) {
      return null;
    }
    return buildTreeCategoryCopyToRight(
      session.rightRoot,
      selectedPath,
      selectedLeft,
    );
  }, [
    treeSelectionStatus,
    selectedLeft,
    selectedPath,
    session.rightRoot,
    rightIsReadOnlyShared,
  ]);

  const pairedBranchMergeTarget = useMemo(() => {
    if (
      !selectedLeft ||
      !selectedRight ||
      selectedLeft.contentCategory ||
      selectedRight.contentCategory ||
      isTypeConflictSelection ||
      selectedPath == null ||
      treeSelectionStatus === 'left-only' ||
      treeSelectionStatus === 'right-only'
    ) {
      return null;
    }
    const branchPath = selectedPath ?? [];
    if (
      !branchSubtreeHasCompareDifferences(session.leftRoot, session.rightRoot, branchPath)
    ) {
      return null;
    }
    return {
      left: {
        mergeIntoCategoryId: selectedLeft.id,
        sourceCategoryId: selectedRight.id,
        treatIntoAsParent: false,
      },
      right: {
        mergeIntoCategoryId: selectedRight.id,
        sourceCategoryId: selectedLeft.id,
        treatIntoAsParent: false,
      },
    };
  }, [
    selectedLeft,
    selectedRight,
    isTypeConflictSelection,
    selectedPath,
    treeSelectionStatus,
    session.leftRoot,
    session.rightRoot,
  ]);

  const pairedContentMergeTargets = useMemo(() => {
    if (
      !selectedLeft?.contentCategory ||
      !selectedRight?.contentCategory ||
      isTypeConflictSelection ||
      selectedPath == null ||
      treeSelectionStatus === 'left-only' ||
      treeSelectionStatus === 'right-only'
    ) {
      return null;
    }
    if (getNodeCompareStatus(selectedLeft, selectedRight) === 'match') {
      return null;
    }
    const leftMissing = getNodeCompareStatusForSide(
      selectedLeft,
      selectedRight,
      'left',
    ) === 'missing-content';
    const rightMissing = getNodeCompareStatusForSide(
      selectedLeft,
      selectedRight,
      'right',
    ) === 'missing-content';
    return {
      left: leftMissing
        ? {
            mergeIntoCategoryId: selectedLeft.id,
            sourceCategoryId: selectedRight.id,
            treatIntoAsParent: false,
          }
        : null,
      right: rightMissing
        ? {
            mergeIntoCategoryId: selectedRight.id,
            sourceCategoryId: selectedLeft.id,
            treatIntoAsParent: false,
          }
        : null,
    };
  }, [
    selectedLeft,
    selectedRight,
    isTypeConflictSelection,
    selectedPath,
    treeSelectionStatus,
  ]);

  const treeCategorySubtreeToLeft = useMemo(() => {
    if (treeCategoryCopyToLeft) {
      return {
        mergeIntoCategoryId: treeCategoryCopyToLeft.targetParentCategoryId,
        sourceCategoryId: treeCategoryCopyToLeft.sourceCategoryId,
        treatIntoAsParent: true,
      };
    }
    return (
      pairedContentMergeTargets?.left ?? pairedBranchMergeTarget?.left ?? null
    );
  }, [treeCategoryCopyToLeft, pairedContentMergeTargets, pairedBranchMergeTarget]);

  const treeCategorySubtreeToRight = useMemo(() => {
    if (treeCategoryCopyToRight) {
      return {
        mergeIntoCategoryId: treeCategoryCopyToRight.targetParentCategoryId,
        sourceCategoryId: treeCategoryCopyToRight.sourceCategoryId,
        treatIntoAsParent: true,
      };
    }
    if (rightIsReadOnlyShared) {
      return null;
    }
    return (
      pairedContentMergeTargets?.right ?? pairedBranchMergeTarget?.right ?? null
    );
  }, [
    treeCategoryCopyToRight,
    pairedContentMergeTargets,
    pairedBranchMergeTarget,
    rightIsReadOnlyShared,
  ]);

  const categoryPanePath = useMemo(() => {
    if (selectedPath == null) {
      return null;
    }
    return selectedPath;
  }, [selectedPath]);

  const categoryPaneLeft = useMemo(
    () =>
      categoryPanePath == null
        ? undefined
        : findCategoryByPath(session.leftRoot, categoryPanePath),
    [categoryPanePath, session.leftRoot],
  );

  const categoryPaneRight = useMemo(
    () =>
      categoryPanePath == null
        ? undefined
        : findCategoryByPath(session.rightRoot, categoryPanePath),
    [categoryPanePath, session.rightRoot],
  );

  const singleSidedBranchCopyPlan = useMemo(() => {
    if (
      !isSingleSidedCategorySelection ||
      selectedPath == null ||
      selectedPath.length === 0
    ) {
      return null;
    }
    const sole = selectedLeft ?? selectedRight;
    if (!sole || categoryIsContentCategory(sole)) {
      return null;
    }
    if (
      treeSelectionStatus !== 'left-only' &&
      treeSelectionStatus !== 'right-only'
    ) {
      return null;
    }
    if (
      treeSelectionStatus === 'right-only' &&
      selectedRight &&
      selectedPath != null
    ) {
      const target = buildTreeCategoryCopyToLeft(
        session.leftRoot,
        selectedPath,
        selectedRight,
      );
      if (!target) {
        return null;
      }
      return {
        direction: 'left' as const,
        branchPath: [...selectedPath],
        targetParentCategoryId: target.targetParentCategoryId,
        newCategoryName: selectedRight.name,
      };
    }
    if (
      treeSelectionStatus === 'left-only' &&
      selectedLeft &&
      !rightIsReadOnlyShared &&
      selectedPath != null
    ) {
      const target = buildTreeCategoryCopyToRight(
        session.rightRoot,
        selectedPath,
        selectedLeft,
      );
      if (!target) {
        return null;
      }
      return {
        direction: 'right' as const,
        branchPath: [...selectedPath],
        targetParentCategoryId: target.targetParentCategoryId,
        newCategoryName: selectedLeft.name,
      };
    }
    return null;
  }, [
    isSingleSidedCategorySelection,
    selectedPath,
    selectedLeft,
    selectedRight,
    treeSelectionStatus,
    session.leftRoot,
    session.rightRoot,
    rightIsReadOnlyShared,
  ]);

  const singleSidedBranchCopyView = useMemo(() => {
    if (!singleSidedBranchCopyPlan || selectedPath == null) {
      return false;
    }
    if (
      relPathToKey(selectedPath) !==
      relPathToKey(singleSidedBranchCopyPlan.branchPath)
    ) {
      return false;
    }
    return !hasOneSidedBranchAncestor(
      session.leftRoot,
      session.rightRoot,
      selectedPath,
    );
  }, [
    singleSidedBranchCopyPlan,
    selectedPath,
    session.leftRoot,
    session.rightRoot,
  ]);

  const canMergeCompare = useMemo(() => {
    if (selectedPath == null) {
      return false;
    }
    const sole = selectedLeft ?? selectedRight;
    const standaloneSingleSidedContentCopy =
      isSingleSidedCategorySelection &&
      categoryIsContentCategory(sole) &&
      !hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath,
      );
    if (standaloneSingleSidedContentCopy) {
      return true;
    }
    if (singleSidedBranchCopyView) {
      return true;
    }
    if (!categoryPaneLeft || !categoryPaneRight) {
      return false;
    }
    return categoryPaneLeft.contentCategory === categoryPaneRight.contentCategory;
  }, [
    selectedPath,
    categoryPaneLeft,
    categoryPaneRight,
    isSingleSidedCategorySelection,
    selectedLeft,
    selectedRight,
    session.leftRoot,
    session.rightRoot,
    singleSidedBranchCopyView,
  ]);

  const singleSidedContentCopyPlan = useMemo(() => {
    if (!isSingleSidedCategorySelection || selectedPath == null) {
      return null;
    }
    if (
      hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath,
      )
    ) {
      return null;
    }

    if (selectedLeft?.contentCategory && !selectedRight) {
      if (rightIsReadOnlyShared) {
        // Right document is view-only shared — copying into it is not allowed.
        return null;
      }
      const parentPath = selectedPath.slice(0, -1);
      const targetParent =
        parentPath.length === 0
          ? session.rightRoot
          : findCategoryByPath(session.rightRoot, parentPath);
      if (!targetParent || targetParent.contentCategory) {
        return null;
      }
      return {
        direction: 'right' as const,
        targetParentCategoryId: targetParent.id,
        newCategoryName: selectedLeft.name,
      };
    }

    if (selectedRight?.contentCategory && !selectedLeft) {
      const parentPath = selectedPath.slice(0, -1);
      const targetParent =
        parentPath.length === 0
          ? session.leftRoot
          : findCategoryByPath(session.leftRoot, parentPath);
      if (!targetParent || targetParent.contentCategory) {
        return null;
      }
      return {
        direction: 'left' as const,
        targetParentCategoryId: targetParent.id,
        newCategoryName: selectedRight.name,
      };
    }

    return null;
  }, [
    isSingleSidedCategorySelection,
    selectedPath,
    selectedLeft,
    selectedRight,
    session.leftRoot,
    session.rightRoot,
    rightIsReadOnlyShared,
  ]);

  const paneCategory = useMemo(() => {
    if (selectedPath == null) {
      return null;
    }
    const root = selectedTreeSide === 'left' ? session.leftRoot : session.rightRoot;
    return selectedPath.length === 0 ? root : findCategoryByPath(root, selectedPath);
  }, [selectedPath, selectedTreeSide, session.leftRoot, session.rightRoot]);

  const comparePaneMode = useMemo((): 'categories' | 'content' | null => {
    if (selectedPath == null || (!selectedLeft && !selectedRight)) {
      return null;
    }
    if (isTypeConflictSelection && paneCategory) {
      if (paneCategory.contentCategory) {
        return 'content';
      }
      return 'categories';
    }
    if (isSingleSidedCategorySelection) {
      const sole = selectedLeft ?? selectedRight!;
      if (sole.contentCategory) {
        return 'content';
      }
      return 'categories';
    }
    if (
      (selectedLeft && !selectedLeft.contentCategory) ||
      (selectedRight && !selectedRight.contentCategory)
    ) {
      return 'categories';
    }
    return 'content';
  }, [
    selectedPath,
    selectedLeft,
    selectedRight,
    isSingleSidedCategorySelection,
    isTypeConflictSelection,
    paneCategory,
  ]);

  const viewingContentUnderSingleSidedBranch = useMemo(
    () =>
      comparePaneMode === 'content' &&
      selectedPath != null &&
      hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath,
      ),
    [comparePaneMode, selectedPath, session.leftRoot, session.rightRoot],
  );

  const comparedCategoryRows = useMemo(() => {
    if (comparePaneMode !== 'categories' || categoryPanePath == null) {
      return null;
    }
    if (isTypeConflictSelection && paneCategory) {
      return buildSingleSideChildCategoryRows(
        categoryPanePath,
        paneCategory,
        selectedTreeSide,
      );
    }
    return buildComparedChildCategoryRows(
      session.leftRoot,
      session.rightRoot,
      categoryPanePath,
      selectedTreeSide,
    );
  }, [
    comparePaneMode,
    categoryPanePath,
    isTypeConflictSelection,
    paneCategory,
    selectedTreeSide,
    session.leftRoot,
    session.rightRoot,
  ]);

  const comparedContentRows = useMemo(() => {
    if (comparePaneMode !== 'content' || selectedPath == null) {
      return null;
    }
    if (isTypeConflictSelection && paneCategory) {
      return buildSingleSideContentRows(paneCategory, selectedTreeSide);
    }
    if (isSingleSidedCategorySelection) {
      const leftEntries = selectedLeft ? collectSubtreeContentEntries(selectedLeft) : [];
      const rightEntries = selectedRight ? collectSubtreeContentEntries(selectedRight) : [];
      return buildComparedSubtreeContentRows(
        leftEntries,
        rightEntries,
        () => undefined,
        () => undefined,
      );
    }
    return buildComparedContentRows(selectedLeft?.contents, selectedRight?.contents);
  }, [
    comparePaneMode,
    selectedPath,
    selectedLeft,
    selectedRight,
    isSingleSidedCategorySelection,
  ]);

  const showContentCategoryColumn = useMemo(
    () =>
      (isSingleSidedCategorySelection || isTypeConflictSelection) &&
      (comparedContentRows?.some((row) => (row.categoryRelPath?.length ?? 0) > 0) ?? false),
    [comparedContentRows, isSingleSidedCategorySelection, isTypeConflictSelection],
  );

  const rightOnlyRowKeys = useMemo(() => {
    if (!comparedContentRows) {
      return [];
    }
    return comparedContentRows
      .filter((row) => row.status === 'right-only')
      .map((row) => comparedRowSelectionKey(row));
  }, [comparedContentRows]);

  const leftOnlyRowKeys = useMemo(() => {
    if (!comparedContentRows || rightIsReadOnlyShared) {
      return [];
    }
    return comparedContentRows
      .filter((row) => row.status === 'left-only')
      .map((row) => comparedRowSelectionKey(row));
  }, [comparedContentRows, rightIsReadOnlyShared]);

  const mergeableRowKeys = useMemo(
    () => [...rightOnlyRowKeys, ...leftOnlyRowKeys],
    [rightOnlyRowKeys, leftOnlyRowKeys],
  );

  const rightOnlyCategoryRowKeys = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows
      .filter((row) => row.status === 'right-only')
      .map((row) => comparedCategorySelectionKey(row));
  }, [comparedCategoryRows]);

  const leftOnlyCategoryRowKeys = useMemo(() => {
    if (!comparedCategoryRows || rightIsReadOnlyShared) {
      return [];
    }
    return comparedCategoryRows
      .filter((row) => row.status === 'left-only')
      .map((row) => comparedCategorySelectionKey(row));
  }, [comparedCategoryRows, rightIsReadOnlyShared]);

  const mergeableCategoryRowKeys = useMemo(
    () => [...rightOnlyCategoryRowKeys, ...leftOnlyCategoryRowKeys],
    [rightOnlyCategoryRowKeys, leftOnlyCategoryRowKeys],
  );

  const typeConflictCategoryRowKeys = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows
      .filter((row) => row.status === 'type-conflict')
      .map((row) => comparedCategorySelectionKey(row));
  }, [comparedCategoryRows]);

  const selectedTypeConflictTableRows = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows.filter(
      (row) =>
        row.status === 'type-conflict' &&
        row.leftCategory &&
        row.rightCategory &&
        selectedRowKeys.has(comparedCategorySelectionKey(row)),
    );
  }, [comparedCategoryRows, selectedRowKeys]);

  const typeConflictRowsForActions = useMemo(() => {
    if (selectedTypeConflictTableRows.length > 0) {
      return selectedTypeConflictTableRows;
    }
    if (
      isTypeConflictSelection &&
      selectedLeft &&
      selectedRight &&
      selectedPath != null &&
      selectedPath.length > 0
    ) {
      return [
        {
          status: 'type-conflict' as const,
          key: treeSelectionRowKey,
          displayName: selectedPath[selectedPath.length - 1],
          childRelPath: selectedPath,
          leftCategory: selectedLeft,
          rightCategory: selectedRight,
        },
      ];
    }
    return [];
  }, [
    selectedTypeConflictTableRows,
    isTypeConflictSelection,
    selectedLeft,
    selectedRight,
    selectedPath,
    treeSelectionRowKey,
  ]);

  const typeConflictMergeActions = useMemo(
    () =>
      typeConflictMergeTargetsForRows(
        session.typeConflictMergedSides,
        typeConflictRowsForActions,
      ),
    [session.typeConflictMergedSides, typeConflictRowsForActions],
  );

  const activeRightOnlyKeys =
    comparePaneMode === 'categories' ? rightOnlyCategoryRowKeys : rightOnlyRowKeys;
  const activeLeftOnlyKeys =
    comparePaneMode === 'categories' ? leftOnlyCategoryRowKeys : leftOnlyRowKeys;
  const activeMergeableKeys =
    comparePaneMode === 'categories' ? mergeableCategoryRowKeys : mergeableRowKeys;

  const selectedPathKey = selectedPath == null ? '' : relPathToKey(selectedPath);

  useEffect(() => {
    // UX: when selecting a one-sided content category, pre-select all rows so the user can
    // copy "the whole category" by default and then unselect what they don't want.
    if (
      onMergeSelectedContent &&
      isSingleSidedCategorySelection &&
      comparePaneMode === 'content' &&
      comparedContentRows &&
      !hasOneSidedBranchAncestor(
        session.leftRoot,
        session.rightRoot,
        selectedPath ?? [],
      )
    ) {
      const next = new Set<string>();
      for (const row of comparedContentRows) {
        if (comparedRowCanSelectForMerge(row, rightIsReadOnlyShared)) {
          next.add(comparedRowSelectionKey(row));
        }
      }
      setSelectedRowKeys(next);
      return;
    }
    setSelectedRowKeys(new Set());
  }, [selectedPathKey]);

  const selectedCopyToLeftIds = useMemo(() => {
    if (!comparedContentRows) {
      return [];
    }
    return comparedContentRows
      .filter(
        (row) =>
          row.status === 'right-only' && selectedRowKeys.has(comparedRowSelectionKey(row)),
      )
      .map((row) => row.content.id);
  }, [comparedContentRows, selectedRowKeys]);

  const selectedCopyToRightIds = useMemo(() => {
    if (!comparedContentRows || rightIsReadOnlyShared) {
      return [];
    }
    return comparedContentRows
      .filter(
        (row) =>
          row.status === 'left-only' && selectedRowKeys.has(comparedRowSelectionKey(row)),
      )
      .map((row) => row.content.id);
  }, [comparedContentRows, selectedRowKeys, rightIsReadOnlyShared]);

  const selectedCopyToLeftCategoryIds = useMemo(() => {
    if (!comparedCategoryRows) {
      return [];
    }
    return comparedCategoryRows
      .filter(
        (row) =>
          row.status === 'right-only' &&
          row.rightCategory &&
          selectedRowKeys.has(comparedCategorySelectionKey(row)),
      )
      .map((row) => row.rightCategory!.id);
  }, [comparedCategoryRows, selectedRowKeys]);

  const selectedCopyToRightCategoryIds = useMemo(() => {
    if (!comparedCategoryRows || rightIsReadOnlyShared) {
      return [];
    }
    return comparedCategoryRows
      .filter(
        (row) =>
          row.status === 'left-only' &&
          row.leftCategory &&
          selectedRowKeys.has(comparedCategorySelectionKey(row)),
      )
      .map((row) => row.leftCategory!.id);
  }, [comparedCategoryRows, selectedRowKeys, rightIsReadOnlyShared]);

  const toggleRowSelection = (selectionKey: string) => {
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (next.has(selectionKey)) {
        next.delete(selectionKey);
      } else {
        next.add(selectionKey);
      }
      return next;
    });
  };

  const toggleSelectAllWithStatus = (status: 'right-only' | 'left-only') => {
    const keys = status === 'right-only' ? activeRightOnlyKeys : activeLeftOnlyKeys;
    if (keys.length === 0) {
      return;
    }
    const allSelected = keys.every((key) => selectedRowKeys.has(key));
    setSelectedRowKeys((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const key of keys) {
          next.delete(key);
        }
      } else {
        for (const key of keys) {
          next.add(key);
        }
      }
      return next;
    });
  };

  const allRightOnlySelected =
    activeRightOnlyKeys.length > 0 &&
    activeRightOnlyKeys.every((key) => selectedRowKeys.has(key));
  const allLeftOnlySelected =
    activeLeftOnlyKeys.length > 0 &&
    activeLeftOnlyKeys.every((key) => selectedRowKeys.has(key));
  const someRightOnlySelected =
    !allRightOnlySelected &&
    activeRightOnlyKeys.some((key) => selectedRowKeys.has(key));
  const someLeftOnlySelected =
    !allLeftOnlySelected &&
    activeLeftOnlyKeys.some((key) => selectedRowKeys.has(key));

  const effectiveCategoryCopyToLeft = useMemo(() => {
    if (
      selectedCopyToLeftCategoryIds.length > 0 &&
      categoryIsBranchCategory(categoryPaneLeft)
    ) {
      return {
        targetParentCategoryId: categoryPaneLeft!.id,
        sourceCategoryIds: selectedCopyToLeftCategoryIds,
      };
    }
    if (treeCategoryCopyToLeft && selectedCopyToLeftCategoryIds.length === 0) {
      return {
        targetParentCategoryId: treeCategoryCopyToLeft.targetParentCategoryId,
        sourceCategoryIds: [treeCategoryCopyToLeft.sourceCategoryId],
      };
    }
    return null;
  }, [
    selectedCopyToLeftCategoryIds,
    categoryPaneLeft,
    treeCategoryCopyToLeft,
  ]);

  const effectiveCategoryCopyToRight = useMemo(() => {
    if (
      selectedCopyToRightCategoryIds.length > 0 &&
      categoryIsBranchCategory(categoryPaneRight)
    ) {
      return {
        targetParentCategoryId: categoryPaneRight!.id,
        sourceCategoryIds: selectedCopyToRightCategoryIds,
      };
    }
    if (treeCategoryCopyToRight && selectedCopyToRightCategoryIds.length === 0) {
      return {
        targetParentCategoryId: treeCategoryCopyToRight.targetParentCategoryId,
        sourceCategoryIds: [treeCategoryCopyToRight.sourceCategoryId],
      };
    }
    return null;
  }, [
    selectedCopyToRightCategoryIds,
    categoryPaneRight,
    treeCategoryCopyToRight,
  ]);

  const effectiveContentCopyToLeft = useMemo(() => {
    if (selectedCopyToLeftIds.length > 0 && selectedLeft?.contentCategory) {
      return {
        targetCategoryId: selectedLeft.id,
        sourceContentIds: selectedCopyToLeftIds,
      };
    }
    return null;
  }, [selectedCopyToLeftIds, selectedLeft]);

  const effectiveContentCopyToRight = useMemo(() => {
    if (selectedCopyToRightIds.length > 0 && selectedRight?.contentCategory) {
      return {
        targetCategoryId: selectedRight.id,
        sourceContentIds: selectedCopyToRightIds,
      };
    }
    return null;
  }, [selectedCopyToRightIds, selectedRight]);

  const copyToLeftCount = useMemo(() => {
    if (singleSidedContentCopyPlan?.direction === 'left') {
      return selectedCopyToLeftIds.length;
    }
    if (
      singleSidedBranchCopyView &&
      singleSidedBranchCopyPlan?.direction === 'left'
    ) {
      return selectedCopyToLeftCategoryIds.length;
    }
    return (
      (effectiveCategoryCopyToLeft?.sourceCategoryIds.length ?? 0) +
      (effectiveContentCopyToLeft?.sourceContentIds.length ?? 0)
    );
  }, [
    singleSidedContentCopyPlan,
    singleSidedBranchCopyView,
    singleSidedBranchCopyPlan,
    selectedCopyToLeftIds,
    selectedCopyToLeftCategoryIds,
    effectiveCategoryCopyToLeft,
    effectiveContentCopyToLeft,
  ]);

  const copyToRightCount = useMemo(() => {
    if (singleSidedContentCopyPlan?.direction === 'right') {
      return selectedCopyToRightIds.length;
    }
    if (
      singleSidedBranchCopyView &&
      singleSidedBranchCopyPlan?.direction === 'right'
    ) {
      return selectedCopyToRightCategoryIds.length;
    }
    return (
      (effectiveCategoryCopyToRight?.sourceCategoryIds.length ?? 0) +
      (effectiveContentCopyToRight?.sourceContentIds.length ?? 0)
    );
  }, [
    singleSidedContentCopyPlan,
    singleSidedBranchCopyView,
    singleSidedBranchCopyPlan,
    selectedCopyToRightIds,
    selectedCopyToRightCategoryIds,
    effectiveCategoryCopyToRight,
    effectiveContentCopyToRight,
  ]);

  const copyToLeftButtonLabel = mergeApplying
    ? 'Copying…'
    : singleSidedContentCopyPlan?.direction === 'left' &&
        selectedCopyToLeftIds.length === 0
      ? 'Copy category only to left'
      : `Copy to left (${copyToLeftCount})`;

  const copyToRightButtonLabel = mergeApplying
    ? 'Copying…'
    : singleSidedContentCopyPlan?.direction === 'right' &&
        selectedCopyToRightIds.length === 0
      ? 'Copy category only to right'
      : `Copy to right (${copyToRightCount})`;

  const isSingleSidedBranchCopyLeft =
    singleSidedBranchCopyView && singleSidedBranchCopyPlan?.direction === 'left';
  const isSingleSidedBranchCopyRight =
    singleSidedBranchCopyView && singleSidedBranchCopyPlan?.direction === 'right';
  const branchSubcategoriesReadyForCopyLeft =
    isSingleSidedBranchCopyLeft &&
    selectedCopyToLeftCategoryIds.length >= MIN_ONE_SIDED_BRANCH_SUBCATEGORIES;
  const branchSubcategoriesReadyForCopyRight =
    isSingleSidedBranchCopyRight &&
    selectedCopyToRightCategoryIds.length >= MIN_ONE_SIDED_BRANCH_SUBCATEGORIES;

  const showCopyToLeftButton =
    singleSidedContentCopyPlan?.direction === 'left' ||
    branchSubcategoriesReadyForCopyLeft ||
    (!singleSidedBranchCopyView && copyToLeftCount > 0);
  const showCopyToRightButton =
    singleSidedContentCopyPlan?.direction === 'right' ||
    branchSubcategoriesReadyForCopyRight ||
    (!singleSidedBranchCopyView && copyToRightCount > 0);

  const handleCopyToLeft = async () => {
    const copyCategoryOnly = singleSidedContentCopyPlan?.direction === 'left';
    const copyBranchWithSubs = branchSubcategoriesReadyForCopyLeft;
    if (
      !onMergeSelectedContent ||
      (!copyCategoryOnly && !copyBranchWithSubs && copyToLeftCount === 0)
    ) {
      return;
    }
    try {
      if (copyBranchWithSubs && singleSidedBranchCopyPlan) {
        await onMergeSelectedContent({
          copyToLeft: {
            createBranchShell: {
              parentCategoryId: singleSidedBranchCopyPlan.targetParentCategoryId,
              name: singleSidedBranchCopyPlan.newCategoryName,
            },
            sourceCategoryIds: selectedCopyToLeftCategoryIds,
          },
        });
      } else if (
        singleSidedContentCopyPlan?.direction === 'left'
      ) {
        const created = await api.post<Category>('/categories', {
          parentId: singleSidedContentCopyPlan.targetParentCategoryId,
          name: singleSidedContentCopyPlan.newCategoryName,
          description: '',
        });
        await onMergeSelectedContent({
          copyToLeft: {
            targetCategoryId: created.data.id,
            sourceContentIds: selectedCopyToLeftIds,
            newlyCreatedCategoryId: created.data.id,
          },
        });
      } else if (effectiveCategoryCopyToLeft) {
        await onMergeSelectedContent({
          copyToLeft: effectiveCategoryCopyToLeft,
        });
      } else if (effectiveContentCopyToLeft) {
        await onMergeSelectedContent({
          copyToLeft: effectiveContentCopyToLeft,
        });
      }
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const handleCopyToRight = async () => {
    const copyCategoryOnly = singleSidedContentCopyPlan?.direction === 'right';
    const copyBranchWithSubs = branchSubcategoriesReadyForCopyRight;
    if (
      !onMergeSelectedContent ||
      rightIsReadOnlyShared ||
      (!copyCategoryOnly && !copyBranchWithSubs && copyToRightCount === 0)
    ) {
      return;
    }
    try {
      if (copyBranchWithSubs && singleSidedBranchCopyPlan) {
        await onMergeSelectedContent({
          copyToRight: {
            createBranchShell: {
              parentCategoryId: singleSidedBranchCopyPlan.targetParentCategoryId,
              name: singleSidedBranchCopyPlan.newCategoryName,
            },
            sourceCategoryIds: selectedCopyToRightCategoryIds,
          },
        });
      } else if (
        singleSidedContentCopyPlan?.direction === 'right'
      ) {
        const created = await api.post<Category>('/categories', {
          parentId: singleSidedContentCopyPlan.targetParentCategoryId,
          name: singleSidedContentCopyPlan.newCategoryName,
          description: '',
        });
        await onMergeSelectedContent({
          copyToRight: {
            targetCategoryId: created.data.id,
            sourceContentIds: selectedCopyToRightIds,
            newlyCreatedCategoryId: created.data.id,
          },
        });
      } else if (effectiveCategoryCopyToRight) {
        await onMergeSelectedContent({
          copyToRight: effectiveCategoryCopyToRight,
        });
      } else if (effectiveContentCopyToRight) {
        await onMergeSelectedContent({
          copyToRight: effectiveContentCopyToRight,
        });
      }
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const paneTitle = comparePaneMode === 'categories' ? 'Categories' : 'Content';
  const activeDocumentLabel =
    selectedTreeSide === 'left' ? session.left.label : session.right.label;
  const panePathLabel = useMemo(() => {
    const pathLabel =
      selectedPath != null
        ? selectedPath.length === 0
          ? selectedTreeSide === 'left'
            ? session.leftRoot.name
            : session.rightRoot.name
          : selectedPath.join(' / ')
        : '';
    if (isTypeConflictSelection && pathLabel) {
      return `${activeDocumentLabel} — ${pathLabel}`;
    }
    if (comparePaneMode === 'categories' && categoryPanePath != null) {
      return categoryPanePath.length === 0
        ? session.leftRoot.name
        : categoryPanePath.join(' / ');
    }
    return pathLabel;
  }, [
    comparePaneMode,
    categoryPanePath,
    selectedPath,
    selectedTreeSide,
    isTypeConflictSelection,
    activeDocumentLabel,
    session.leftRoot.name,
    session.rightRoot.name,
  ]);
  const showCategoryCheckboxes =
    canMergeCompare || typeConflictCategoryRowKeys.length > 0;

  const showCopyActions =
    !isTypeConflictSelection &&
    !viewingContentUnderSingleSidedBranch &&
    activeMergeableKeys.length > 0;

  /** Type-conflict merge for the tree selection (whole category), not row picks in the pane. */
  const showHeaderTypeConflictMerge =
    !!onMergeSelectedContent &&
    selectedPath != null &&
    selectedTypeConflictTableRows.length === 0 &&
    typeConflictRowsForActions.length > 0 &&
    (typeConflictMergeActions.canLeft || typeConflictMergeActions.canRight);

  /** Type-conflict merge only for rows explicitly selected in the category/content table. */
  const showDetailPaneTypeConflictMerge =
    !!onMergeSelectedContent &&
    selectedTypeConflictTableRows.length > 0 &&
    typeConflictRowsForActions.length > 0 &&
    (typeConflictMergeActions.canLeft || typeConflictMergeActions.canRight);

  const showDetailPaneSelectiveActions =
    showCopyActions ||
    showDetailPaneTypeConflictMerge ||
    (canMergeCompare && activeMergeableKeys.length > 0) ||
    (canMergeCompare && typeConflictCategoryRowKeys.length > 0);

  const showDetailPaneActions =
    !!onMergeSelectedContent &&
    selectedPath != null &&
    (comparePaneMode === 'content' ||
      comparePaneMode === 'categories' ||
      isTypeConflictSelection) &&
    showDetailPaneSelectiveActions;

  const categoryPanelMessage = useMemo(() => {
    if (selectedPath == null) {
      if (documentsFullyMatch) {
        return 'No differences between these documents. Select a category in either tree to browse structure and content.';
      }
      return 'Select a category in either tree to compare subcategories or content rows.';
    }
    if (isRootSelection && !documentsFullyMatch) {
      return 'Document roots selected. Use Merge to left or Merge to right. Check “Merge into a new document” to create a new library document instead of updating the open one.';
    }
    return null;
  }, [selectedPath, documentsFullyMatch, isRootSelection]);

  const handleMergeCategorySubtreeToLeft = async () => {
    if (mergeApplying) {
      return;
    }
    if (
      isRootSelection &&
      mergeIntoNewDocument &&
      onMergeDocumentsToNew &&
      treeCategorySubtreeToLeft != null
    ) {
      openMergeToNewDocumentDialog('left');
      return;
    }
    if (!onMergeSelectedContent || !treeCategorySubtreeToLeft) {
      return;
    }
    try {
      await onMergeSelectedContent({
        mergeCategorySubtreeToLeft: treeCategorySubtreeToLeft,
      });
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const handleMergeCategorySubtreeToRight = async () => {
    if (mergeApplying || rightIsReadOnlyShared) {
      return;
    }
    if (
      isRootSelection &&
      mergeIntoNewDocument &&
      onMergeDocumentsToNew &&
      treeCategorySubtreeToRight != null
    ) {
      openMergeToNewDocumentDialog('right');
      return;
    }
    if (!onMergeSelectedContent || !treeCategorySubtreeToRight) {
      return;
    }
    try {
      await onMergeSelectedContent({
        mergeCategorySubtreeToRight: treeCategorySubtreeToRight,
      });
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const handleMergeTypeConflict = async (direction: MergeCompareTypeConflictDirection) => {
    if (!onMergeSelectedContent || typeConflictRowsForActions.length === 0 || mergeApplying) {
      return;
    }
    const rowsToMerge = typeConflictRowsForActions.filter((row) => {
      const done = session.typeConflictMergedSides?.[row.key] ?? [];
      if (direction === 'left') {
        return !done.includes('left');
      }
      return !done.includes(direction);
    });
    if (rowsToMerge.length === 0) {
      return;
    }
    try {
      await onMergeSelectedContent({
        typeConflict: {
          direction,
          rows: rowsToMerge.map((row) => ({
            rowKey: row.key,
            leftCategoryId: row.leftCategory!.id,
            rightCategoryId: row.rightCategory!.id,
          })),
        },
      });
      setSelectedRowKeys(new Set());
    } catch {
      // MainLayout shows the error alert
    }
  };

  const categoryRowBackground = (status: CompareNodeStatus) => {
    switch (status) {
      case 'match':
        return alpha(theme.palette.success.main, 0.1);
      case 'has-child-diff':
        return alpha(theme.palette.warning.main, 0.12);
      case 'missing-content':
        return alpha(theme.palette.warning.main, 0.12);
      case 'left-only':
        return alpha(theme.palette.info.main, 0.12);
      case 'right-only':
        return alpha(theme.palette.warning.main, 0.12);
      case 'type-conflict':
        return alpha(theme.palette.error.main, 0.1);
      case 'unpaired':
        return undefined;
    }
  };

  const contentRowBackground = (status: ComparedContentRowStatus) => {
    switch (status) {
      case 'both':
        return alpha(theme.palette.success.main, 0.1);
      case 'left-only':
        return alpha(theme.palette.info.main, 0.12);
      case 'right-only':
        return alpha(theme.palette.warning.main, 0.12);
    }
  };

  const selectAllSideButtonSx = (
    side: 'right-only' | 'left-only',
    state: 'none' | 'some' | 'all',
  ) => {
    const paletteKey = side === 'right-only' ? 'warning' : 'info';
    const main = theme.palette[paletteKey].main;
    const dark = theme.palette[paletteKey].dark;
    const base = {
      minWidth: 0,
      px: 1,
      py: 0.125,
      fontSize: '0.7rem',
      textTransform: 'none' as const,
    };
    if (state === 'all') {
      return {
        ...base,
        fontWeight: 600,
        border: `1px solid ${alpha(main, 0.55)}`,
        bgcolor: alpha(main, 0.28),
        color: dark,
        boxShadow: `inset 0 0 0 1px ${alpha(main, 0.15)}`,
        '&:hover': { bgcolor: alpha(main, 0.36) },
      };
    }
    if (state === 'some') {
      return {
        ...base,
        fontWeight: 500,
        border: `1px dashed ${alpha(main, 0.65)}`,
        bgcolor: alpha(main, 0.16),
        color: 'text.primary',
        '&:hover': { bgcolor: alpha(main, 0.22) },
      };
    }
    return {
      ...base,
      fontWeight: 400,
      border: `1px solid ${alpha(main, 0.35)}`,
      bgcolor: contentRowBackground(side),
      color: 'text.secondary',
      '&:hover': { bgcolor: alpha(main, 0.18), color: dark },
    };
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <CompareHelpTooltip helpKey="document-merge-compare" placement="bottom">
          <Typography variant="subtitle2" color="text.secondary" sx={{ mr: 0.5 }}>
            Merge compare — Left: <strong>{session.left.label}</strong> · Right:{' '}
            <strong>{session.right.label}</strong>
            {rightIsReadOnlyShared ? ' (view only)' : ''}
            {documentsFullyMatch ? (
              <>
                {' — '}
                <Box
                  component="span"
                  sx={{ color: 'success.main', fontWeight: 600 }}
                >
                  These documents are identical.
                </Box>
              </>
            ) : null}
          </Typography>
        </CompareHelpTooltip>
        {onMergeSettingsChange ? (
          <>
            <DropdownFieldTooltip title={getTooltipTextForState('merge-document-missing-resolution', true)}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="compare-missing-resolve-label">Missing</InputLabel>
              <Select
                labelId="compare-missing-resolve-label"
                label="Missing"
                value={resolveMissing}
                onChange={(e) =>
                  onMergeSettingsChange(e.target.value as MergeMissingResolve, resolveTypeMismatch)
                }
              >
                <MenuItem value={MergeMissingResolve.COPY}>Copy</MenuItem>
                <MenuItem value={MergeMissingResolve.SKIP}>Skip</MenuItem>
                <MenuItem value={MergeMissingResolve.ABORT}>Abort</MenuItem>
              </Select>
              </FormControl>
            </DropdownFieldTooltip>
            <DropdownFieldTooltip title={getTooltipTextForState('merge-document-type-resolution', true)}>
              <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="compare-type-resolve-label">Type mismatch</InputLabel>
              <Select
                labelId="compare-type-resolve-label"
                label="Type mismatch"
                value={resolveTypeMismatch}
                onChange={(e) =>
                  onMergeSettingsChange(resolveMissing, e.target.value as MergeTypeMismatchResolve)
                }
              >
                <MenuItem value={MergeTypeMismatchResolve.KEEP}>Keep</MenuItem>
                <MenuItem value={MergeTypeMismatchResolve.SKIP}>Skip</MenuItem>
                <MenuItem value={MergeTypeMismatchResolve.ABORT}>Abort</MenuItem>
              </Select>
              </FormControl>
            </DropdownFieldTooltip>
            {onUndoLastCopy && canUndoLastCopy ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-undo-last-copy"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  disabled={mergeApplying}
                  onClick={() => void onUndoLastCopy()}
                >
                  {mergeApplying ? 'Undoing…' : 'Undo last copy'}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {onMergeSelectedContent && treeCategorySubtreeToLeft != null ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-merge-to-left"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeCategorySubtreeToLeft()}
                >
                  {mergeApplying ? 'Merging…' : 'Merge to left'}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {onMergeSelectedContent &&
            !rightIsReadOnlyShared &&
            treeCategorySubtreeToRight != null ? (
              <CompareHelpTooltip helpKey="document-merge-compare-merge-to-right" enabled={!mergeApplying}>
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeCategorySubtreeToRight()}
                >
                  {mergeApplying ? 'Merging…' : 'Merge to right'}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {showHeaderTypeConflictMerge && typeConflictMergeActions.canLeft ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-type-conflict-merge"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="warning"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeTypeConflict('left')}
                >
                  {mergeApplying
                    ? 'Merging…'
                    : `Merge to left (${typeConflictRowsForActions.length})`}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {showHeaderTypeConflictMerge &&
            !rightIsReadOnlyShared &&
            typeConflictMergeActions.canRight ? (
              <CompareHelpTooltip
                helpKey="document-merge-compare-type-conflict-merge"
                enabled={!mergeApplying}
              >
                <Button
                  size="small"
                  variant="contained"
                  color="info"
                  disabled={mergeApplying}
                  onClick={() => void handleMergeTypeConflict('right')}
                >
                  {mergeApplying
                    ? 'Merging…'
                    : `Merge to right (${typeConflictRowsForActions.length})`}
                </Button>
              </CompareHelpTooltip>
            ) : null}
            {showMergeIntoNewDocumentOption ? (
              <CompareHelpTooltip helpKey="document-merge-compare-new-document" enabled={!mergeApplying}>
                <FormControlLabel
                  control={
                    <Checkbox
                      size="small"
                      checked={mergeIntoNewDocument}
                      onChange={(e) => setMergeIntoNewDocument(e.target.checked)}
                      disabled={mergeApplying}
                    />
                  }
                  label="Merge into a new document"
                  sx={{ mr: 0, ml: 0.5 }}
                />
              </CompareHelpTooltip>
            ) : null}
          </>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
        <SplitPane
          persistKey="document_compare_trees_detail_split"
          initialSplitPosition={56}
          minSplitPosition={30}
          maxSplitPosition={75}
          leftPane={
            <SplitPane
              persistKey="document_compare_left_right_split"
              initialSplitPosition={50}
              minSplitPosition={20}
              maxSplitPosition={80}
              leftPane={
                <CompareHelpTooltip helpKey="document-merge-compare-trees" placement="right" block>
                <Box sx={{ height: '100%', px: 0.5, py: 0.5 }}>
                  <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={leftExpanded}
                    selected={leftTreeSelectedNodeId}
                    onNodeToggle={(_, nodeIds) => handleChevronToggle('left', nodeIds)}
                    onNodeSelect={(_, nodeId) => selectPathFromNodeId('left', nodeId)}
                    sx={compareDocumentTreeViewSx}
                  >
                    {renderRootTreeItem(
                      'left',
                      session.leftRoot,
                      session.rightRoot,
                      leftRootNodeId,
                      session.leftRoot.name,
                    )}
                  </TreeView>
                </Box>
                </CompareHelpTooltip>
              }
              rightPane={
                <CompareHelpTooltip helpKey="document-merge-compare-trees" placement="left" block>
                <Box sx={{ height: '100%', px: 0.5, py: 0.5 }}>
                  <TreeView
                    defaultCollapseIcon={<ExpandMoreIcon />}
                    defaultExpandIcon={<ChevronRightIcon />}
                    expanded={rightExpanded}
                    selected={rightTreeSelectedNodeId}
                    onNodeToggle={(_, nodeIds) => handleChevronToggle('right', nodeIds)}
                    onNodeSelect={(_, nodeId) => selectPathFromNodeId('right', nodeId)}
                    sx={compareDocumentTreeViewSx}
                  >
                    {renderRootTreeItem(
                      'right',
                      session.rightRoot,
                      session.leftRoot,
                      rightRootNodeId,
                      session.rightRoot.name,
                    )}
                  </TreeView>
                </Box>
                </CompareHelpTooltip>
              }
            />
          }
          rightPane={
        <Box sx={{ height: '100%', minWidth: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Box
            sx={{
              px: 2,
              py: 0.75,
              flexShrink: 0,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {showDetailPaneActions ? (
              <CompareHelpTooltip helpKey="document-merge-compare-detail-pane" placement="bottom">
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, mr: 0.5 }}>
                  {paneTitle}
                  {panePathLabel ? ` — ${panePathLabel}` : ''}
                </Typography>
                {showCopyActions && activeRightOnlyKeys.length > 0 && (
                  <CompareHelpTooltip helpKey="document-merge-compare-select-all-right">
                  <Button
                    size="small"
                    variant="text"
                    disableElevation
                    sx={selectAllSideButtonSx(
                      'right-only',
                      allRightOnlySelected ? 'all' : someRightOnlySelected ? 'some' : 'none',
                    )}
                    onClick={() => toggleSelectAllWithStatus('right-only')}
                  >
                    {allRightOnlySelected
                      ? `All right selected (${activeRightOnlyKeys.length})`
                      : `Select all right (${activeRightOnlyKeys.length})`}
                  </Button>
                  </CompareHelpTooltip>
                )}
                {showCopyActions && activeLeftOnlyKeys.length > 0 && (
                  <CompareHelpTooltip helpKey="document-merge-compare-select-all-left">
                  <Button
                    size="small"
                    variant="text"
                    disableElevation
                    sx={selectAllSideButtonSx(
                      'left-only',
                      allLeftOnlySelected ? 'all' : someLeftOnlySelected ? 'some' : 'none',
                    )}
                    onClick={() => toggleSelectAllWithStatus('left-only')}
                  >
                    {allLeftOnlySelected
                      ? `All left selected (${activeLeftOnlyKeys.length})`
                      : `Select all left (${activeLeftOnlyKeys.length})`}
                  </Button>
                  </CompareHelpTooltip>
                )}
                {(showCopyActions || showDetailPaneTypeConflictMerge) && (
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {showDetailPaneTypeConflictMerge && typeConflictMergeActions.canLeft && (
                      <CompareHelpTooltip
                        helpKey="document-merge-compare-type-conflict-merge"
                        enabled={!mergeApplying}
                      >
                      <Button
                        size="small"
                        variant="contained"
                        color="warning"
                        disabled={mergeApplying}
                        onClick={() => void handleMergeTypeConflict('left')}
                      >
                        {mergeApplying
                          ? 'Merging…'
                          : `Merge to left (${typeConflictRowsForActions.length})`}
                      </Button>
                      </CompareHelpTooltip>
                    )}
                    {showDetailPaneTypeConflictMerge &&
                    !rightIsReadOnlyShared &&
                    typeConflictMergeActions.canRight && (
                      <CompareHelpTooltip
                        helpKey="document-merge-compare-type-conflict-merge"
                        enabled={!mergeApplying}
                      >
                      <Button
                        size="small"
                        variant="contained"
                        color="info"
                        disabled={mergeApplying}
                        onClick={() => void handleMergeTypeConflict('right')}
                      >
                        {mergeApplying
                          ? 'Merging…'
                          : `Merge to right (${typeConflictRowsForActions.length})`}
                      </Button>
                      </CompareHelpTooltip>
                    )}
                    {showCopyActions && (
                      <>
                        {showCopyToLeftButton && (
                          <CompareHelpTooltip
                            helpKey="document-merge-compare-copy-to-left"
                            enabled={
                              !mergeApplying &&
                              (singleSidedContentCopyPlan?.direction === 'left' ||
                                branchSubcategoriesReadyForCopyLeft ||
                                copyToLeftCount > 0)
                            }
                          >
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            disabled={
                              mergeApplying ||
                              (singleSidedContentCopyPlan?.direction === 'left'
                                ? false
                                : isSingleSidedBranchCopyLeft
                                  ? !branchSubcategoriesReadyForCopyLeft
                                  : copyToLeftCount === 0)
                            }
                            onClick={() => void handleCopyToLeft()}
                          >
                            {copyToLeftButtonLabel}
                          </Button>
                          </CompareHelpTooltip>
                        )}
                        {showCopyToRightButton && (
                          <CompareHelpTooltip
                            helpKey="document-merge-compare-copy-to-right"
                            enabled={
                              !mergeApplying &&
                              (singleSidedContentCopyPlan?.direction === 'right' ||
                                branchSubcategoriesReadyForCopyRight ||
                                copyToRightCount > 0)
                            }
                          >
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            disabled={
                              mergeApplying ||
                              (singleSidedContentCopyPlan?.direction === 'right'
                                ? false
                                : isSingleSidedBranchCopyRight
                                  ? !branchSubcategoriesReadyForCopyRight
                                  : copyToRightCount === 0)
                            }
                            onClick={() => void handleCopyToRight()}
                          >
                            {copyToRightButtonLabel}
                          </Button>
                          </CompareHelpTooltip>
                        )}
                      </>
                    )}
                  </Box>
                )}
                {rightIsReadOnlyShared && canMergeCompare && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ width: '100%', mt: 0.25 }}
                  >
                    Right document is view-only shared — left-only items cannot be copied there.
                  </Typography>
                )}
              </Box>
              </CompareHelpTooltip>
            ) : (
              <CompareHelpTooltip helpKey="document-merge-compare-detail-pane" placement="bottom">
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {paneTitle}
                {panePathLabel ? ` — ${panePathLabel}` : ''}
              </Typography>
              </CompareHelpTooltip>
            )}
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {categoryPanelMessage && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>
                {categoryPanelMessage}
              </Typography>
            )}
            {isTypeConflictSelection && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0 }}>
                Type conflict — showing {activeDocumentLabel}. Click the other tree to inspect
                that side.
                {showHeaderTypeConflictMerge
                  ? ' Use Merge to left or Merge to right in the header for this category.'
                  : typeConflictCategoryRowKeys.length > 0
                    ? ' Select rows below to merge selectively, or use Copy to left/right.'
                    : ''}
              </Typography>
            )}
            {singleSidedBranchCopyView && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0 }}>
                Select at least two subcategories to copy with this branch. Each selected
                subcategory is copied with its full subtree. Use Merge to left/right in the header
                to copy the entire branch instead.
              </Typography>
            )}
            {viewingContentUnderSingleSidedBranch && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 1.5, pb: 0 }}>
                Content rows cannot be copied from here. Select the parent branch in the tree and
                choose subcategories to copy, or use Merge to copy the whole branch.
              </Typography>
            )}
            {comparedCategoryRows && (
              <Table
                size="small"
                stickyHeader
                sx={{
                  fontSize: 'var(--app-font-size, 14px)',
                  '& .MuiTableCell-root': {
                    padding: '4px 8px',
                    fontSize: 'var(--app-font-size, 14px)',
                  },
                  '& .MuiTableRow-root': {
                    height: 36,
                    cursor: 'pointer',
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {showCategoryCheckboxes ? (
                      <TableCell padding="checkbox" sx={{ width: 42 }} />
                    ) : null}
                    <TableCell sx={{ width: 36 }}>Type</TableCell>
                    <TableCell>Name</TableCell>
                    {showCategoryCheckboxes ? (
                      <TableCell sx={{ width: 100 }}>Match</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparedCategoryRows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={showCategoryCheckboxes ? 4 : 2}>
                        <Typography variant="body2" color="text.secondary">
                          No subcategories under this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    comparedCategoryRows.map((row) => {
                      const node =
                        row.leftCategory ?? row.rightCategory;
                      const selectionKey = comparedCategorySelectionKey(row);
                      const canSelect =
                        !!onMergeSelectedContent &&
                        comparedCategoryCanSelectForMerge(row, rightIsReadOnlyShared) &&
                        (canMergeCompare || row.status === 'type-conflict');
                      const isPathSelected =
                        selectedPath != null &&
                        selectedPath.length === row.childRelPath.length &&
                        row.childRelPath.every((p, i) => selectedPath[i] === p);
                      return (
                        <TableRow
                          key={row.key}
                          hover
                          selected={isPathSelected}
                          onClick={() => {
                            const side =
                              row.status === 'right-only'
                                ? 'right'
                                : row.status === 'left-only'
                                  ? 'left'
                                  : selectedTreeSide;
                            selectPath(side, row.childRelPath);
                          }}
                          sx={{
                            backgroundColor: categoryRowBackground(row.status),
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: alpha(
                                row.status === 'match'
                                  ? theme.palette.success.main
                                  : row.status === 'left-only'
                                    ? theme.palette.info.main
                                    : row.status === 'right-only' ||
                                        row.status === 'missing-content' ||
                                        row.status === 'has-child-diff'
                                      ? theme.palette.warning.main
                                      : theme.palette.error.main,
                                0.18,
                              ),
                            },
                          }}
                        >
                          {showCategoryCheckboxes ? (
                            <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                              {canSelect ? (
                                <Checkbox
                                  size="small"
                                  checked={selectedRowKeys.has(selectionKey)}
                                  onChange={() => toggleRowSelection(selectionKey)}
                                  onClick={(e) => e.stopPropagation()}
                                  inputProps={{ 'aria-label': `Select ${row.displayName}` }}
                                />
                              ) : null}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            {renderCompareChildCategoryTypeIcon(node, row.status)}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{row.displayName}</Typography>
                          </TableCell>
                          {showCategoryCheckboxes ? (
                            <TableCell>
                              {row.status !== 'unpaired' ? (
                                <CompareStatusChip status={row.status} />
                              ) : null}
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
            {comparedContentRows && (
              <Table
                size="small"
                stickyHeader
                sx={{
                  fontSize: 'var(--app-font-size, 14px)',
                  '& .MuiTableCell-root': {
                    padding: '4px 8px',
                    fontSize: 'var(--app-font-size, 14px)',
                  },
                  '& .MuiTableRow-root': {
                    height: 32,
                  },
                  '& .MuiTableHead-root .MuiTableCell-root': {
                    backgroundColor: theme.palette.background.paper,
                    fontWeight: 600,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    {canMergeCompare ? (
                      <TableCell padding="checkbox" sx={{ width: 42 }} />
                    ) : null}
                    <TableCell sx={{ width: 36 }} />
                    {showContentCategoryColumn ? (
                      <TableCell sx={{ width: '18%' }}>Category</TableCell>
                    ) : null}
                    <TableCell sx={{ width: '20%' }}>Subcase</TableCell>
                    <TableCell>Content</TableCell>
                    {canMergeCompare ? (
                      <TableCell sx={{ width: 100 }}>Match</TableCell>
                    ) : null}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparedContentRows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={
                          3 +
                          (showContentCategoryColumn ? 1 : 0) +
                          (canMergeCompare ? 2 : 0)
                        }
                      >
                        <Typography variant="body2" color="text.secondary">
                          No content rows in this category.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    comparedContentRows.map((row) => {
                      const { content, status } = row;
                      const selectionKey = comparedRowSelectionKey(row);
                      const canSelect =
                        canMergeCompare &&
                        !!onMergeSelectedContent &&
                        comparedRowCanSelectForMerge(row, rightIsReadOnlyShared);
                      return (
                        <TableRow
                          key={row.key}
                          hover
                          selected={canSelect && selectedRowKeys.has(selectionKey)}
                          sx={{
                            backgroundColor: contentRowBackground(status),
                            '&:hover': {
                              backgroundColor: alpha(
                                status === 'both'
                                  ? theme.palette.success.main
                                  : status === 'left-only'
                                    ? theme.palette.info.main
                                    : theme.palette.warning.main,
                                0.18,
                              ),
                            },
                          }}
                        >
                          {canMergeCompare ? (
                            <TableCell padding="checkbox">
                              {canSelect ? (
                                <Checkbox
                                  size="small"
                                  checked={selectedRowKeys.has(selectionKey)}
                                  onChange={() => toggleRowSelection(selectionKey)}
                                  onClick={(e) => e.stopPropagation()}
                                  inputProps={{ 'aria-label': `Select ${content.subcase}` }}
                                />
                              ) : null}
                            </TableCell>
                          ) : null}
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <StickyNote2OutlinedIcon
                                sx={{ color: 'text.secondary', fontSize: 20 }}
                                aria-hidden
                              />
                              {contentHasMedia(content) ? (
                                <Tooltip
                                  title={getContentMediaIndicatorTooltip(content)}
                                  placement="top"
                                >
                                  <PermMediaOutlinedIcon
                                    sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }}
                                    aria-label="Has media"
                                  />
                                </Tooltip>
                              ) : null}
                            </Box>
                          </TableCell>
                          {showContentCategoryColumn ? (
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  color:
                                    (row.categoryRelPath?.length ?? 0) > 0
                                      ? 'text.primary'
                                      : 'text.secondary',
                                }}
                              >
                                {(row.categoryRelPath?.length ?? 0) > 0
                                  ? row.categoryRelPath!.join(' / ')
                                  : '—'}
                              </Typography>
                            </TableCell>
                          ) : null}
                          <TableCell>{content.subcase || '(no subcase)'}</TableCell>
                          <TableCell>
                            <Typography
                              component="span"
                              variant="body2"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: content.text.trim() ? 'text.primary' : 'text.secondary',
                                fontStyle: content.text.trim() ? 'normal' : 'italic',
                              }}
                            >
                              {formatContentPreview(content)}
                            </Typography>
                          </TableCell>
                          {canMergeCompare ? (
                            <TableCell>
                              <CompareContentStatusChip status={status} />
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            )}
          </Box>
        </Box>
          }
        />
      </Box>

      <Dialog
        open={mergeToNewDialogOpen}
        onClose={mergeApplying ? undefined : closeMergeToNewDocumentDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>New document name</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {mergeToNewBaseSide && mergeToNewOtherSide ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Creates a new document from <strong>{mergeToNewBaseSide.label}</strong> and merges{' '}
              <strong>{mergeToNewOtherSide.label}</strong> into it.
            </Typography>
          ) : null}
          {loadingMergeToNewSubcategories ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">Loading groups…</Typography>
            </Box>
          ) : (
            <CompareHelpTooltip helpKey="merge-document-new" block>
              <TextField
                autoFocus
                fullWidth
                label="New document name"
                placeholder="Document name — merged"
                value={mergeToNewName}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  setMergeToNewName(e.target.value);
                  setMergeToNewNameError(
                    validateMergeToNewDocumentName(e.target.value.trim()),
                  );
                }}
                error={!!mergeToNewNameError}
                helperText={mergeToNewNameError}
                margin="dense"
                disabled={mergeApplying}
                sx={{ width: '100%' }}
              />
            </CompareHelpTooltip>
          )}
        </DialogContent>
        <DialogActions>
          <CompareHelpTooltip helpKey="cancel">
            <Button onClick={closeMergeToNewDocumentDialog} disabled={mergeApplying}>
              Cancel
            </Button>
          </CompareHelpTooltip>
          <CompareHelpTooltip
            helpKey="merge-document-merge"
            enabled={
              !mergeApplying &&
              !loadingMergeToNewSubcategories &&
              !!mergeToNewName.trim() &&
              !validateMergeToNewDocumentName(mergeToNewName.trim())
            }
          >
            <Button
              variant="contained"
              disabled={
                mergeApplying ||
                loadingMergeToNewSubcategories ||
                !mergeToNewName.trim() ||
                !!validateMergeToNewDocumentName(mergeToNewName.trim())
              }
              onClick={() => void handleMergeToNewDocumentConfirm()}
            >
              {mergeApplying ? 'Merging…' : 'Merge'}
            </Button>
          </CompareHelpTooltip>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentCompareView;
